// Shared helpers for the P&L views (full Performance tab + account mini
// chart). The backend buckets the daily series in ET; everything here
// anchors keys and labels to that same calendar regardless of the viewer's
// timezone. Day buckets use epoch-day integers derived from the ET calendar
// date (stepping raw +24h drifts off the ET-midnight instants across DST);
// hour buckets are exact hour instants, so integer epoch-hours are exact and
// +1 stepping is DST-proof (hours are uniform in UTC).

export const DAY_MS = 24 * 60 * 60 * 1000;
export const HOUR_MS = 60 * 60 * 1000;

const ET_DAY_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function toEtEpochDay(instant) {
  const [y, m, d] = ET_DAY_FORMAT.format(new Date(instant))
    .split('-')
    .map(Number);
  return Date.UTC(y, m - 1, d) / DAY_MS;
}

export function epochDayLabel(epochDay, withYear) {
  return new Date(epochDay * DAY_MS).toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
  });
}

export function toEpochHour(instant) {
  return Math.floor(new Date(instant).getTime() / HOUR_MS);
}

export function epochHourLabel(epochHour) {
  return new Date(epochHour * HOUR_MS).toLocaleString(undefined, {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
  });
}

// Zero-fill the sparse bucket series and accumulate into chart points.
// `daily` is the endpoint's series ([{ day, pnl }]); granularity 'hour'|'day'.
export function buildChartPoints(daily, granularity, { withYear = false } = {}) {
  if (!daily.length) return [];
  const hourly = granularity === 'hour';
  const toBucket = hourly ? toEpochHour : toEtEpochDay;
  const toLabel = hourly
    ? epochHourLabel
    : (bucket) => epochDayLabel(bucket, withYear);
  const byBucket = new Map(daily.map((d) => [toBucket(d.day), d.pnl]));
  const first = toBucket(daily[0].day);
  const last = toBucket(daily[daily.length - 1].day);
  const points = [];
  let cumulative = 0;
  for (let bucket = first; bucket <= last; bucket += 1) {
    const dayPnl = byBucket.get(bucket) || 0;
    cumulative += dayPnl;
    points.push({
      dayPnl,
      cumulative,
      dateLabel: toLabel(bucket),
    });
  }
  return points;
}

export function formatSignedMoney(value) {
  const rounded = Math.round(value);
  const abs = Math.abs(rounded).toLocaleString();
  if (rounded < 0) return `-$${abs}`;
  return `+$${abs}`;
}
